import { Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { ColorDirective, TypographyDirective } from '@dartcounter/ui';
import { EventOverlayService, OverlayText } from '@services/event-overlay.service';

@Component({
    selector: 'app-event-overlay',
    templateUrl: './event-overlay.component.html',
    styleUrls: ['./event-overlay.component.css'],
    standalone: true,
    imports: [TypographyDirective, ColorDirective],
})
export class EventOverlayComponent implements OnInit {
    protected eventOverlayService: EventOverlayService = inject(EventOverlayService);

    public isVisible: WritableSignal<boolean> = signal(false);
    public isHiding: WritableSignal<boolean> = signal(false);
    public texts: WritableSignal<OverlayText[]> = signal([]);
    public hideDuration: WritableSignal<number> = signal(0);

    private overlayTimeout: ReturnType<typeof setTimeout> | null = null;

    public ngOnInit(): void {
        this.eventOverlayService.overlayStatus.subscribe(({ isVisible, texts, duration }) => {
            if (isVisible) {
                this.texts.set(texts || []);
                this.hideDuration.set(Math.max(0, ...this.texts().map((text) => text.hideDuration ?? 0)));
                this.isVisible.set(true);
                this.isHiding.set(false);

                this.clearTimeout();

                const maxDelay = this.texts().length
                    ? Math.max(...this.texts().map((t) => (t.delay || 0) + (t.duration || 0)))
                    : 0;

                const ms = (duration ?? maxDelay) || 0;
                this.overlayTimeout = setTimeout(() => this.startHiding(), ms);
            }
        });

        this.eventOverlayService.hideOverlayManually.subscribe(() => {
            this.clearTimeout();
            this.startHiding();
        });
    }

    private startHiding(): void {
        if (!this.isVisible() || this.isHiding()) return;

        this.isHiding.set(true);

        if (!this.hideDuration()) {
            this.finishHiding();
        } else {
            this.overlayTimeout = setTimeout(() => this.finishHiding(), this.hideDuration() + 50);
        }
    }

    private finishHiding(): void {
        this.clearTimeout();
        this.isHiding.set(false);
        this.isVisible.set(false);
        this.texts.set([]);
    }

    private clearTimeout(): void {
        if (this.overlayTimeout) {
            clearTimeout(this.overlayTimeout);
            this.overlayTimeout = null;
        }
    }
}
